import React, { useEffect, useState } from "react";
import {
  Card,
  Table,
  Tag,
  Input,
  notification,
  Form,
  Row,
  Col,
  Button,
  Modal,
} from "antd";
import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";

dayjs.extend(customParseFormat);
import { useSelector } from "react-redux";
import PageContentHeader from "../../../components/common/PageContentHeader";
import Title from "antd/es/skeleton/Title";
import {
  getPendingRequestsListService,
  approveRejectLeadRequestService,
} from "../../../services/admin-services";
import { getLeadRequestTypesService } from "../../../services/master-services";
import { openLeadViewInNewWindow } from "../../../utills/helpers";

const { Search } = Input;
const { TextArea } = Input;

const PendingRequests = () => {
  const { isAdmin } = useSelector((state) => state.auth);
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });
  const [requestTypes, setRequestTypes] = useState([]);

  // Approve/Reject modal state
  const [isApproveRejectModalOpen, setIsApproveRejectModalOpen] =
    useState(false);
  const [approveRejectForm] = Form.useForm();
  const [currentRecord, setCurrentRecord] = useState(null);
  const [approvalAction, setApprovalAction] = useState(null); // 'approve' or 'reject'
  const [approveRejectLoading, setApproveRejectLoading] = useState(false);

  const fetchPendingRequests = async (
    page = pagination.current,
    perPage = pagination.pageSize,
    searchTerm = search
  ) => {
    try {
      setLoading(true);
      const params = {
        page,
        per_page: perPage,
      };

      if (searchTerm && searchTerm.trim()) {
        params.search = searchTerm.trim();
      }

      const res = await getPendingRequestsListService(params);
      const list = res?.data?.data?.list_data || res?.data?.list_data || [];
      const total =
        (res?.data?.data?.total_records || res?.data?.total_records) ?? 0;

      const rows = Array.isArray(list)
        ? list.map((item) => {
            // Find request_type_id from request_type_name
            // First check if API response includes request_type_id
            let requestTypeId = item.request_type_id || null;
            
            // If not in response, try to map from request_type_name
            if (!requestTypeId && item.request_type_name) {
              const requestType = requestTypes.find(
                (type) =>
                  type.request_type === item.request_type_name ||
                  type.request_type_name === item.request_type_name ||
                  type.id === item.request_type_id
              );
              requestTypeId = requestType?.id || null;
            }

            return {
              key: item.id,
              id: item.id,
              lead_id: item.lead_id || "-",
              lead_number: item.lead_id ? `${item.lead_id}` : "-",
              customer_name: item.customer_name || "-",
              vehicle: item.vehicle || "-",
              customer_area: item.customer_area || "-",
              request_type_name: item.request_type_name || "-",
              request_type_id: requestTypeId,
              chassis_number: item.chassis_number || null,
              request_status: item.request_status || "Pending",
              created_at: item.created_at || "-",
              approved_rejected_date: item.approved_rejected_date || "-",
              updated_at: item.updated_at || "-",
            };
          })
        : [];

      setData(rows);
      setPagination((prev) => ({
        ...prev,
        current: page,
        pageSize: perPage,
        total: total,
      }));
    } catch (error) {
      notification.error({
        message: "Failed to load pending requests",
        description: error?.response?.data?.message || error?.message,
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadRequestTypes();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (requestTypes.length > 0) {
      fetchPendingRequests();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [requestTypes]);

  const loadRequestTypes = async () => {
    try {
      const requestTypesRes = await getLeadRequestTypesService();
      const requestTypes = requestTypesRes?.data?.data || [];
      setRequestTypes(requestTypes);
    } catch (error) {
      console.error("Failed to load request types:", error);
    }
  };

  const handleSearch = (value) => {
    const trimmedValue = value?.trim() || "";
    setSearch(trimmedValue);
    fetchPendingRequests(1, pagination.pageSize, trimmedValue);
  };

  const handleApprove = (record) => {
    setCurrentRecord(record);
    setApprovalAction("approve");
    setIsApproveRejectModalOpen(true);
    approveRejectForm.resetFields();
  };

  const handleReject = (record) => {
    setCurrentRecord(record);
    setApprovalAction("reject");
    setIsApproveRejectModalOpen(true);
    approveRejectForm.resetFields();
  };

  const handleApproveRejectCancel = () => {
    setIsApproveRejectModalOpen(false);
    setCurrentRecord(null);
    setApprovalAction(null);
    approveRejectForm.resetFields();
  };

  const handleApproveRejectSubmit = async (values) => {

    if (!currentRecord || !currentRecord.lead_id) {
      notification.error({
        description: "Missing required information",
      });
      return;
    }
    if (!currentRecord.request_type_id) {
      notification.error({
        description: "Request type ID is missing. Please refresh the page and try again.",
      });
      return;
    }

    try {
      setApproveRejectLoading(true);

      const payload = {
        lead_id: currentRecord.lead_id,
        request_id: currentRecord.id,
        request_type_id: currentRecord.request_type_id,
        approval_status: approvalAction === "approve" ? 1 : 2,
        approve_reject_remarks:
          approvalAction === "reject"
            ? values?.remarks || ""
            : currentRecord.approve_reject_remarks || "",
      };

      const res = await approveRejectLeadRequestService(payload);

      if (res?.data?.status === 200 || res?.data?.status === 201) {
        notification.success({
          message:
            res?.data?.message ||
            `${approvalAction === "approve" ? "Approved" : "Rejected"
            } successfully`,
        });
        setIsApproveRejectModalOpen(false);
        setCurrentRecord(null);
        setApprovalAction(null);
        approveRejectForm.resetFields();

        // Refresh the pending requests list
        fetchPendingRequests(
          pagination.current,
          pagination.pageSize,
          search
        );
      } else {
        notification.error({
          description:
            res?.data?.message ||
            `Failed to ${approvalAction === "approve" ? "approve" : "reject"
            } request`,
        });
      }
    } catch (error) {
      notification.error({
        description: error?.response?.data?.message || error?.message,
      });
    } finally {
      setApproveRejectLoading(false);
    }
  };

  const columns = [
    {
      title: "Lead Number",
      dataIndex: "lead_number",
      key: "lead_number",
      render: (text, record) => {
        if (!text || text === "-" || !record.lead_id) return "-";
        return (
          <a
            onClick={() => {
              openLeadViewInNewWindow(isAdmin, record.lead_id);
            }}
            style={{ cursor: "pointer", color: "#1890ff" }}
          >
            {text}
          </a>
        );
      },
    },
    {
      title: "Customer Name",
      dataIndex: "customer_name",
      key: "customer_name",
      render: (text) => text || "-",
    },
    {
      title: "Vehicle",
      dataIndex: "vehicle",
      key: "vehicle",
      render: (text) => text || "-",
    },
    {
      title: "Customer Area",
      dataIndex: "customer_area",
      key: "customer_area",
      render: (text) => text || "-",
    },
    {
      title: "Request Type",
      dataIndex: "request_type_name",
      key: "request_type_name",
      render: (text) => text || "-",
    },
    {
      title: "Request Status",
      dataIndex: "request_status",
      key: "request_status",
      render: (status) => {
        if (!status || status === "-") return "-";
        let color = "gold";
        const statusLower = status.toLowerCase();
        if (statusLower === "approved") color = "green";
        else if (statusLower === "rejected") color = "red";
        else if (statusLower === "pending") color = "gold";
        return <Tag color={color}>{status}</Tag>;
      },
    },
    {
      title: "Created At",
      dataIndex: "created_at",
      key: "created_at",
      render: (text) => {
        if (!text || text === "-") return "-";
        // Try to parse and format the date
        try {
          // Handle format like "May 01 2024" - try parsing with custom format
          if (typeof text === "string" && text.includes(" ")) {
            // Try parsing "May 01 2024" format
            const parsedDate = dayjs(text, "MMMM DD YYYY");
            if (parsedDate.isValid()) {
              return parsedDate.format("DD/MM/YYYY");
            }
            // If that fails, try standard parsing
            const standardDate = dayjs(text);
            if (standardDate.isValid()) {
              return standardDate.format("DD/MM/YYYY");
            }
            return text;
          }
          // Standard date format (ISO, etc.)
          const date = dayjs(text);
          if (date.isValid()) {
            return date.format("DD/MM/YYYY");
          }
          return text;
        } catch (error) {
          return text;
        }
      },
    },
    {
      title: "Last Updated",
      dataIndex: "updated_at",
      key: "updated_at",
      render: (text) => {
        if (!text || text === "-") return "-";
        // Try to parse and format the date
        try {
          // Handle format like "May 01 2024" - try parsing with custom format
          if (typeof text === "string" && text.includes(" ")) {
            // Try parsing "May 01 2024" format
            const parsedDate = dayjs(text, "MMMM DD YYYY");
            if (parsedDate.isValid()) {
              return parsedDate.format("DD/MM/YYYY");
            }
            // If that fails, try standard parsing
            const standardDate = dayjs(text);
            if (standardDate.isValid()) {
              return standardDate.format("DD/MM/YYYY");
            }
            return text;
          }
          // Standard date format (ISO, etc.)
          const date = dayjs(text);
          if (date.isValid()) {
            return date.format("DD/MM/YYYY");
          }
          return text;
        } catch (error) {
          return text;
        }
      },
    },
    {
      title: "Actions",
      key: "actions",
      fixed: "right",
      width: 150,
      render: (_, record) => {
        // Only show actions if status is Pending
        const isPending =
          record.request_status?.toLowerCase() === "pending" ||
          !record.request_status ||
          record.request_status === "-";

        if (!isPending) return "-";

        return (
          <div style={{ display: "flex", gap: "8px" }}>
            <Button
              type="primary"
              size="small"
              style={{
                backgroundColor: "#0a9551",
                borderColor: "#0a9551",
              }}
              onClick={() => handleApprove(record)}
            >
              Approve
            </Button>
            <Button
              danger
              size="small"
              onClick={() => handleReject(record)}
            >
              Reject
            </Button>
          </div>
        );
      },
    },
  ];

  return (
    <div className="user-data">
      <PageContentHeader title="Pending Requests" />
      <div className="personal-detail">
        <div className="user-left d-flex align-items-center">
          <Title level={4} className="pe-2 pb-2">
            Requests &gt; Pending Requests
          </Title>
        </div>
        <Card className="mb-4">
          <Form layout="vertical">
            <Row gutter={[16, 16]}>
              <Col xs={24} sm={24} md={24} lg={6} xl={6} xxl={6}>
                <Form.Item label="Search" style={{ marginBottom: 0 }}>
                  <Search
                    placeholder="Search by Lead Number, Customer, Vehicle"
                    allowClear
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    onSearch={handleSearch}
                    onPressEnter={(e) => handleSearch(e.target.value)}
                    className="form-control-ant"
                  />
                </Form.Item>
              </Col>
            </Row>
          </Form>
        </Card>

        <Table
          size="middle"
          columns={columns}
          dataSource={data}
          loading={loading}
          rowKey="id"
          pagination={{
            current: pagination.current,
            pageSize: pagination.pageSize,
            total: pagination.total,
            showSizeChanger: true,
            showTotal: (total) => `Total ${total} records`,
          }}
          onChange={(paginationConfig) => {
            const { current, pageSize } = paginationConfig;
            fetchPendingRequests(current, pageSize, search);
          }}
          scroll={{ x: "max-content" }}
        />
      </div>

      {/* Approve/Reject Modal */}
      <Modal
        title={
          <div>
            <h5
              className="text-center mb-0"
              style={{ fontSize: "20px", fontWeight: "500" }}
            >
              Confirmation
            </h5>
            <hr style={{ margin: "15px 0 0 0" }} />
          </div>
        }
        open={isApproveRejectModalOpen}
        onCancel={handleApproveRejectCancel}
        footer={[
          <Button
            key="cancel"
            onClick={handleApproveRejectCancel}
            ghost
            danger
            className="primary-button"
            disabled={approveRejectLoading}
          >
            Cancel
          </Button>,
          <Button
            key="submit"
            type="primary"
            className="secondary-button"
            onClick={() => approveRejectForm.submit()}
            loading={approveRejectLoading}
            style={
              approvalAction === "reject"
                ? { backgroundColor: "#ff4d4f", borderColor: "#ff4d4f" }
                : {}
            }
          >
            {approvalAction === "approve" ? "Approve" : "Reject"}
          </Button>,
        ]}
        maskClosable={false}
        width={600}
      >
        <Form
          form={approveRejectForm}
          layout="vertical"
          onFinish={handleApproveRejectSubmit}
        >
          <div style={{ padding: "20px 0" }}>
            {currentRecord && (
              <p
                style={{
                  fontSize: "16px",
                  marginBottom: "20px",
                  textAlign: "center",
                }}
              >
                You are about to{" "}
                <strong>
                  {approvalAction === "approve" ? "Approve" : "Reject"}
                </strong>{" "}
                the <strong>{currentRecord.request_type_name || "Request"}</strong>
                {" "}for the Customer{" "}
                <strong>{currentRecord.customer_name || "N/A"}</strong>
                . Please click{" "}
                <strong>
                  {approvalAction === "approve" ? "Approve" : "Reject"}
                </strong>{" "}
                to proceed or close the box.
              </p>
            )}
            {approvalAction === "reject" && (
              <Form.Item
                name="remarks"
                label="Remarks"
                rules={[
                  { required: true, message: "Please enter remarks" },
                  {
                    max: 200,
                    message: "Remarks cannot exceed 200 characters",
                  },
                ]}
              >
                <TextArea
                  rows={4}
                  placeholder="Enter remarks"
                  maxLength={200}
                  showCount
                />
              </Form.Item>
            )}
          </div>
        </Form>
      </Modal>
    </div>
  );
};

export default PendingRequests;

